import React from 'react';
import { useNavigate } from 'react-router-dom';
import './BookingConfirmationPopup.css';

const BookingConfirmationPopup = ({ isOpen, onClose, bookingDetails }) => {
  const navigate = useNavigate();
  
  if (!isOpen) return null;

  const {
    bookingId,
    myvistaBookingId,
    myvistaBookingCode,
    qrCodeUrl,
    movieTitle,
    cinemaName,
    cinemaAddress,
    showDate,
    showTime,
    screenName,
    seats,
    ticketCount,
    totalAmount,
    promotionalItems = [],
    pricing = {}
  } = bookingDetails || {};
  
  const bookingStatus = (
    bookingDetails?.status ||
    bookingDetails?.uiStatus ||
    'pending'
  ).toLowerCase();

const isConfirmed = ['confirmed', 'completed'].includes(bookingStatus);
const isPending = bookingStatus === 'pending';

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
      return dateString;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      // If it's already in 12-hour format, return as is
      if (timeString.includes('PM') || timeString.includes('AM')) {
        return timeString;
      }
      // Convert 24-hour to 12-hour format
      const [hours, minutes] = timeString.split(':');
      const hour12 = parseInt(hours) % 12 || 12;
      const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
      return `${hour12}:${minutes} ${ampm}`;
    } catch (e) {
      return timeString;
    }
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  // Group seats by category for BMS-style display.
  // Returns: [{ categoryLabel: 'PLATINUM', seats: ['D7', 'D8'] }, ...]
  const groupSeatsByCategory = (rawSeats) => {
    if (!Array.isArray(rawSeats) || rawSeats.length === 0) {
      return [];
    }

    // Backward-compat: if a plain string was passed (legacy callers), wrap it
    if (typeof rawSeats === 'string') {
      return [{ categoryLabel: null, seats: [rawSeats] }];
    }

    const labelFor = (seat) => {
      const raw =
        seat?.ticketTypeDescription ||
        seat?.areaName ||
        (seat?.category
          ? String(seat.category).charAt(0).toUpperCase() +
            String(seat.category).slice(1)
          : null);
      return raw ? String(raw).toUpperCase() : null;
    };

    const numberFor = (seat) =>
      seat?.displayNumber ||
      `${seat?.row || ''}${seat?.number || ''}` ||
      seat?.id ||
      seat?.name ||
      '';

    const groupMap = new Map();
    rawSeats.forEach((seat) => {
      const label = labelFor(seat) || 'SEAT';
      if (!groupMap.has(label)) {
        groupMap.set(label, []);
      }
      const num = numberFor(seat);
      if (num) groupMap.get(label).push(num);
    });

    return Array.from(groupMap.entries()).map(([categoryLabel, seats]) => ({
      categoryLabel,
      seats,
    }));
  };

  const seatGroups = groupSeatsByCategory(seats);
  const computedTicketCount =
    typeof ticketCount === 'number' && ticketCount > 0
      ? ticketCount
      : Array.isArray(seats)
        ? seats.length
        : 0;

  const handleViewBookings = (e) => {
    e.stopPropagation(); // 🔥 prevent overlay click
    navigate('/profile?tab=my-bookings');
  };

  const handleDone = () => {
    // console.log('handleDone called - navigating to home');
    if (onClose) {
      onClose();
    } else {
      navigate('/');
    }
  };

  return (
    <div
      className="booking-confirmation-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleDone();
        }
      }}
    >
      <div
        className="booking-confirmation-popup"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Title with small success icon */}
        <div className="confirmation-header"></div>
        <h2 className="confirmation-title">
          {isPending ? "Booking Pending" : "Booking Confirmed"}
        </h2>

        <p className="confirmation-subtitle">
          {isPending
            ? "Your booking is pending. Please wait for confirmation."
            : "Your tickets have been successfully booked"}
        </p>

        {/* Booking Details */}
        <div className="booking-details">
          <div className="detail-row">
            <span className="detail-label">Booking ID:</span>
            <span className="detail-value">
              {isConfirmed
                ? myvistaBookingCode || myvistaBookingId || bookingId
                : "Will be available after confirmation"}
            </span>
          </div>
          {qrCodeUrl && isConfirmed && (
            <div className="qr-code-section">
              <div className="qr-code-container">
                <img
                  src={qrCodeUrl}
                  alt="Booking QR Code"
                  className="qr-code-image"
                />
                <p className="qr-code-label">Scan this QR code at the cinema</p>
              </div>
            </div>
          )}

          {movieTitle && (
            <div className="detail-row">
              <span className="detail-label">Movie:</span>
              <span className="detail-value">{movieTitle}</span>
            </div>
          )}

          {(cinemaName || cinemaAddress) && (
            <div className="detail-row">
              <span className="detail-label">Cinema:</span>
              <span className="detail-value">
                {cinemaName && cinemaAddress
                  ? `${cinemaName}, ${cinemaAddress}`
                  : cinemaName || cinemaAddress}
              </span>
            </div>
          )}

          {(showDate || showTime) && (
            <div className="detail-row">
              <span className="detail-label">Show:</span>
              <span className="detail-value">
                {showDate && formatDate(showDate)}{" "}
                {showTime && `• ${formatTime(showTime)}`}
              </span>
            </div>
          )}

          {computedTicketCount > 0 && (
            <div className="detail-row">
              <span className="detail-label">Tickets:</span>
              <span className="detail-value">
                <span className="ticket-count-badge">
                  {computedTicketCount} Ticket{computedTicketCount > 1 ? 's' : ''}
                </span>
              </span>
            </div>
          )}

          {seats && (
            <div className="detail-row detail-row-seats">
              <span className="detail-label">Seats:</span>
              <span className="detail-value detail-value-seats">
                {screenName ? `${screenName} — ` : ''}
                {seatGroups.length > 0 ? (
                  seatGroups.map((group, idx) => (
                    <span key={idx} className="seat-group">
                      {group.categoryLabel && (
                        <span className="seat-group-label">
                          {group.categoryLabel}
                        </span>
                      )}
                      {group.categoryLabel && ' - '}
                      {group.seats.join(', ')}
                      {idx < seatGroups.length - 1 && (
                        <span className="seat-group-sep"> • </span>
                      )}
                    </span>
                  ))
                ) : (
                  <span>
                    {Array.isArray(seats) ? seats.join(', ') : seats}
                  </span>
                )}
              </span>
            </div>
          )}

          {/* Pricing Breakdown */}
          {(() => {
            const ticketPrice = Number(pricing?.ticketPrice || 0);
            const snacksAmount = Number(pricing?.snacksAmount || 0);
            const convenienceBase = Number(pricing?.convenienceFeeBase || 0);
            const convenienceGST = Number(pricing?.convenienceFeeGST || 0);
            const couponDiscount = Number(pricing?.couponDiscount || 0);

            const convenienceTotal = convenienceBase + convenienceGST;

            const totalPaid = Number(pricing?.totalAmount ?? totalAmount ?? 0);

            if (
              ticketPrice === 0 &&
              snacksAmount === 0 &&
              convenienceTotal === 0 &&
              totalPaid === 0
            )
              return null;

            return (
              <>
                {ticketPrice > 0 && (
                  <div className="detail-row">
                    <span className="detail-label">Ticket Price:</span>
                    <span className="detail-value">
                      ₹{ticketPrice.toFixed(2)}
                    </span>
                  </div>
                )}

                {snacksAmount > 0 && (
                  <div className="detail-row">
                    <span className="detail-label">Snacks:</span>
                    <span className="detail-value">
                      ₹{snacksAmount.toFixed(2)}
                    </span>
                  </div>
                )}

                {Array.isArray(promotionalItems) && promotionalItems.length > 0 && (
                  <div
                    className="detail-row"
                    style={{ color: "#28a745", fontWeight: 500 }}
                  >
                    <span className="detail-label">
                      🎁 {promotionalItems[0].name} (Website Launch Offer):
                    </span>
                    <span className="detail-value">FREE</span>
                  </div>
                )}

                {convenienceTotal > 0 && (
                  <>
                    {/* Total Convenience Fee */}
                    <div className="detail-row">
                      <span className="detail-label">Convenience Fee:</span>
                      <span className="detail-value">
                        ₹{convenienceTotal.toFixed(2)}
                      </span>
                    </div>

                    {/* Breakdown (Indented Under It) */}
                    <div
                      className="detail-row"
                      style={{
                        paddingLeft: "20px",
                        fontSize: "0.9em",
                        color: "#6c757d",
                      }}
                    >
                      <span>Base Amount:</span>
                      <span>₹{convenienceBase.toFixed(2)}</span>
                    </div>

                    <div
                      className="detail-row"
                      style={{
                        paddingLeft: "20px",
                        fontSize: "0.9em",
                        color: "#6c757d",
                      }}
                    >
                      <span>IGST @ 18%:</span>
                      <span>₹{convenienceGST.toFixed(2)}</span>
                    </div>
                  </>
                )}

                {couponDiscount > 0 && (
                  <>
                    {/* Original Total Before Discount */}
                    <div className="detail-row">
                      <span className="detail-label">Original Total:</span>
                      <span
                        className="detail-value"
                        style={{ color: "#6c757d" }}
                      >
                        ₹{(totalPaid + couponDiscount).toFixed(2)}
                      </span>
                    </div>

                    {/* Coupon Discount */}
                    <div className="detail-row">
                      <span className="detail-label">Coupon Discount:</span>
                      <span
                        className="detail-value"
                        style={{ color: "#28a745", fontWeight: 600 }}
                      >
                        -₹{couponDiscount.toFixed(2)}
                      </span>
                    </div>
                  </>
                )}

                <div className="detail-row highlight">
                  <span className="detail-label">Total Amount Paid:</span>
                  <span className="detail-value">₹{totalPaid.toFixed(2)}</span>
                </div>
              </>
            );
          })()}
        </div>

        {/* QR Code Section
        {qrCodeUrl && (
          <div className="qr-code-section">
            <div className="qr-code-container">
              <img src={qrCodeUrl} alt="Booking QR Code" className="qr-code-image" />
              <p className="qr-code-label">Scan this QR code at the cinema</p>
            </div>
          </div>
        )} */}

        {/* Info Message */}
        <div className="confirmation-info">
          <p>
            A confirmation email/SMS has been sent to your registered contact
            details.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="confirmation-actions">
          <button
            className="primary-btn confirmation-btn-back"
            onClick={handleBackToHome}
          >
            <i className="fas fa-home"></i>
            Back to Home
          </button>
          <button
            className="secondary-btn confirmation-btn-bookings"
            onClick={handleViewBookings}
          >
            <i className="fas fa-ticket-alt"></i>
            My Bookings
          </button>
          <button
            className="primary-btn confirmation-btn-done"
            onClick={handleDone}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmationPopup;

